import { useCallback, useState } from "react";

/**
 * Hook that gates destructive actions behind a confirmation modal when a data layer
 * has existing field associations. Returns modal state/handlers and a wrapper function
 * that either shows the confirmation or executes immediately.
 */
export function useFieldAssociationConfirmation(
	dataLayerHasExistingFieldAssociations: boolean,
	onResetExistingFieldAssociations: () => void,
) {
	const [fieldAssocModalIsOpen, setFieldAssocModalIsOpen] = useState(false);
	const [deferredFieldAssocAction, setDeferredFieldAssocAction] = useState<(() => void) | undefined>(undefined);

	const withFieldAssociationConfirmation = useCallback(
		(action: () => void) => {
			if (dataLayerHasExistingFieldAssociations) {
				setDeferredFieldAssocAction(() => action);
				setFieldAssocModalIsOpen(true);
			} else {
				action();
			}
		},
		[dataLayerHasExistingFieldAssociations],
	);

	const handleFieldAssocModalOk = useCallback(() => {
		setFieldAssocModalIsOpen(false);
		onResetExistingFieldAssociations();
		deferredFieldAssocAction?.();
		setDeferredFieldAssocAction(undefined);
	}, [deferredFieldAssocAction, onResetExistingFieldAssociations]);

	const handleFieldAssocModalCancel = useCallback(() => {
		setFieldAssocModalIsOpen(false);
		setDeferredFieldAssocAction(undefined);
	}, []);

	return {
		fieldAssocModalIsOpen,
		handleFieldAssocModalOk,
		handleFieldAssocModalCancel,
		withFieldAssociationConfirmation,
	};
}
